(() => {
  if (window.productCollectionBreadcrumbLoaded) return;
  window.productCollectionBreadcrumbLoaded = true;

  const RETURN_KEY = 'breadcrumbReturn';
  const CARD_LINK_SELECTOR = '#product-grid a[href*="/products/"]';
  const PAUSE_AT_TOP = 400;
  const HOLD_LIMIT = 2000;
  const RECORD_TTL = 30 * 60 * 1000;

  const currentUrl = () => window.location.pathname + window.location.search;

  const pathOf = (url) => new URL(url, window.location.origin).pathname;

  const isPlainClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const clearReturn = () => {
    try {
      sessionStorage.removeItem(RETURN_KEY);
    } catch {
      return;
    }
  };

  const readReturn = () => {
    try {
      const state = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null');
      if (!state) return null;
      if (Date.now() - state.at > RECORD_TTL) {
        clearReturn();
        return null;
      }
      return state;
    } catch {
      return null;
    }
  };

  const writeReturn = (state) => {
    try {
      sessionStorage.setItem(RETURN_KEY, JSON.stringify({ ...state, at: Date.now() }));
      return true;
    } catch {
      return false;
    }
  };

  const setScrollRestoration = (mode) => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = mode;
  };

  const jumpTo = (top) => window.scrollTo({ top, behavior: 'instant' });

  const gridIsRebuilding = () => new URLSearchParams(window.location.search).has('feature');

  const whenPainted = (callback) => {
    const afterPaint = () => requestAnimationFrame(() => requestAnimationFrame(callback));
    if (document.readyState === 'complete') {
      afterPaint();
      return;
    }
    window.addEventListener('load', afterPaint, { once: true });
  };

  const travelToCard = (cardId) => {
    jumpTo(0);

    const controller = new AbortController();
    const { signal } = controller;
    const release = () => {
      controller.abort();
      setScrollRestoration('auto');
    };

    window.addEventListener(
      'scroll',
      () => {
        if (window.scrollY !== 0) jumpTo(0);
      },
      { signal },
    );
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((type) => {
      window.addEventListener(type, release, { passive: true, signal });
    });
    window.setTimeout(release, HOLD_LIMIT);

    whenPainted(() => {
      window.setTimeout(() => {
        if (signal.aborted) return;
        release();
        document.getElementById(cardId)?.scrollIntoView({ block: 'center' });
      }, PAUSE_AT_TOP);
    });
  };

  const handleArrival = () => {
    const state = readReturn();
    if (!state) {
      setScrollRestoration('auto');
      return;
    }

    const { card, ...origin } = state;
    if (card && document.getElementById(card) && !gridIsRebuilding()) {
      if (origin.from) writeReturn(origin);
      else clearReturn();
      travelToCard(card);
      return;
    }

    setScrollRestoration('auto');
    if (origin.from !== currentUrl() || typeof origin.scrollY !== 'number') return;
    if (gridIsRebuilding()) return;
    clearReturn();
    jumpTo(origin.scrollY);
    whenPainted(() => jumpTo(origin.scrollY));
  };

  const wireGrid = () => {
    if (!document.getElementById('product-grid')) return;

    document.addEventListener('click', (event) => {
      const { target } = event;
      if (!isPlainClick(event) || !target?.closest?.(CARD_LINK_SELECTOR)) return;
      if (writeReturn({ from: currentUrl(), scrollY: Math.round(window.scrollY) })) {
        setScrollRestoration('manual');
      }
    });

    handleArrival();
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) handleArrival();
    });
  };

  const wireBreadcrumb = () => {
    const breadcrumb = document.querySelector('.product-breadcrumb');
    const link = breadcrumb?.querySelector('a[href]');
    if (!link) return;

    const targetPath = pathOf(link.href);
    const state = readReturn();
    let cameFromTarget = false;

    if (state?.from && pathOf(state.from) === targetPath) {
      if (state.on) {
        cameFromTarget = state.on === window.location.pathname;
      } else {
        writeReturn({ ...state, on: window.location.pathname });
        cameFromTarget = true;
      }
    }

    if (!cameFromTarget && document.referrer) {
      try {
        const referrer = new URL(document.referrer);
        cameFromTarget =
          referrer.origin === window.location.origin && referrer.pathname === targetPath;
      } catch {
        cameFromTarget = false;
      }
    }

    breadcrumb.querySelectorAll('a[href]').forEach((crumbLink) => {
      crumbLink.addEventListener('click', (event) => {
        if (!isPlainClick(event)) return;
        event.preventDefault();

        writeReturn({ ...(readReturn() ?? {}), card: breadcrumb.dataset.cardId });

        if (cameFromTarget && window.history.length > 1) {
          window.history.back();
          return;
        }
        window.location.replace(crumbLink.href);
      });
    });
  };

  wireGrid();
  wireBreadcrumb();
})();
